import _merge from 'lodash/merge';
import DafProfileModel, {
  DEFAULT_DAF_UNIT,
  IDAFYearly,
  TDAFUnitKeys
} from 'volta/models/daf-profile';
import {
  IDAFWeekly,
  IYearZoneDef,
  IZoneDef,
  TDAFUnit,
  TDAFZoneUnit,
  TZone
} from 'volta/models/types/planning-settings';
import {
  dafKeyFromZone,
  dafsByYear,
  DEFAULT_DAF,
  TZoneDefUnits,
  YEAR_ZONE_DEF
} from 'volta/utils/daf-utils';

import { action } from '@ember/object';
import { capitalize, classify } from '@ember/string';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

export interface IDafEditorSubmit extends IDAFYearly {
  dafProfileId?: string;
}

interface IArgs extends IDafEditorSubmit {
  skuId?: string;
  dafFrequencyUnit: TDAFUnit;
  dafProfiles?: DafProfileModel[];
  onSubmit?: (data: IDafEditorSubmit) => void;
}

export default class DafEditor extends Component<IArgs> {
  // Getters/CPs
  // ~~~~
  @tracked subtitle = {
    dafUnit: 'MONTHLY',
    zoneValues: Object.values(YEAR_ZONE_DEF)
  };

  get selectedDafProfile() {
    const { dafProfiles, dafProfileId } = this.args;
    return (dafProfiles ?? []).find((d) => d.id === dafProfileId);
  }

  get dafs(): IDAFYearly {
    return {
      dafUnit: this.args.dafUnit,
      dafGreenUnit: this.args.dafGreenUnit,
      dafRedBaseUnit: this.args.dafRedBaseUnit,
      dafRedSafetyUnit: this.args.dafRedSafetyUnit,
      dafYellowUnit: this.args.dafYellowUnit,
      dafLtUnit: this.args.dafLtUnit,
      dafWeekly: this.args.dafWeekly,
      dafMonthly: this.args.dafMonthly,
      dafRedBaseWeekly: this.args.dafRedBaseWeekly,
      dafRedBaseMonthly: this.args.dafRedBaseMonthly,
      dafRedSafetyWeekly: this.args.dafRedSafetyWeekly,
      dafRedSafetyMonthly: this.args.dafRedSafetyMonthly,
      dafYellowWeekly: this.args.dafYellowWeekly,
      dafYellowMonthly: this.args.dafYellowMonthly,
      dafGreenWeekly: this.args.dafGreenWeekly,
      dafGreenMonthly: this.args.dafGreenMonthly,
      dafLtWeekly: this.args.dafLtWeekly,
      dafLtMonthly: this.args.dafLtMonthly
    };
  }

  get dafsByYear() {
    return dafsByYear(this.dafs);
  }

  // Actions
  // ~~~~

  @action
  onUpdateSubtitle(dafUnit: TDAFUnit, yearZoneDef: Record<TZone, IZoneDef>) {
    this.subtitle = {
      dafUnit: dafUnit,
      zoneValues: Object.values(yearZoneDef)
    };
  }

  @action
  removeYear(selectedZoneKey: TZone, dafFrequencyUnit: TDAFUnit, year: number) {
    const csKey = `daf${selectedZoneKey === 'adu' ? '' : classify(selectedZoneKey)}${capitalize(
      dafFrequencyUnit.toLowerCase()
    )}` as keyof Omit<IDAFYearly, TDAFUnitKeys>;

    this.args.onSubmit?.({ ...this.dafs, [csKey]: { ...this.dafs[csKey], [year]: undefined } });
  }

  @action
  onSubmit(
    updatedZoneDefs: IYearZoneDef,
    zoneUnits: TZoneDefUnits,
    filledYears: number[],
    selectedDafProfile?: DafProfileModel
  ) {
    this.args.onSubmit?.(
      this.getDataToSubmit(updatedZoneDefs, zoneUnits, filledYears, selectedDafProfile)
    );
  }

  // Helper functions
  // ~~~~

  getDataToSubmit(
    updatedZoneDefs: IYearZoneDef,
    zoneUnits: TZoneDefUnits,
    filledYears: number[],
    selectedDafProfile?: DafProfileModel
  ): IDafEditorSubmit {
    const dafProfileId = selectedDafProfile?.id;

    const dafs: IDAFYearly = DEFAULT_DAF;
    let zoneDefs = { ...updatedZoneDefs };
    for (const year of filledYears) {
      if (!zoneDefs[year]) {
        zoneDefs = _merge({}, zoneDefs, {
          [year]: YEAR_ZONE_DEF
        });
      }

      dafs.dafWeekly[year] = this.orProfileElseZoneDefValue(
        'adu',
        'weekly',
        year,
        zoneDefs,
        selectedDafProfile
      );
      dafs.dafMonthly[year] = this.orProfileElseZoneDefValue(
        'adu',
        'monthly',
        year,
        zoneDefs,
        selectedDafProfile
      );

      dafs.dafRedBaseWeekly[year] = this.orProfileElseZoneDefValue(
        'redBase',
        'weekly',
        year,
        zoneDefs,
        selectedDafProfile
      );
      dafs.dafRedBaseMonthly[year] = this.orProfileElseZoneDefValue(
        'redBase',
        'monthly',
        year,
        zoneDefs,
        selectedDafProfile
      );

      dafs.dafRedSafetyWeekly[year] = this.orProfileElseZoneDefValue(
        'redSafety',
        'weekly',
        year,
        zoneDefs,
        selectedDafProfile
      );
      dafs.dafRedSafetyMonthly[year] = this.orProfileElseZoneDefValue(
        'redSafety',
        'monthly',
        year,
        zoneDefs,
        selectedDafProfile
      );

      dafs.dafYellowWeekly[year] = this.orProfileElseZoneDefValue(
        'yellow',
        'weekly',
        year,
        zoneDefs,
        selectedDafProfile
      );
      dafs.dafYellowMonthly[year] = this.orProfileElseZoneDefValue(
        'yellow',
        'monthly',
        year,
        zoneDefs,
        selectedDafProfile
      );

      dafs.dafGreenWeekly[year] = this.orProfileElseZoneDefValue(
        'green',
        'weekly',
        year,
        zoneDefs,
        selectedDafProfile
      );
      dafs.dafGreenMonthly[year] = this.orProfileElseZoneDefValue(
        'green',
        'monthly',
        year,
        zoneDefs,
        selectedDafProfile
      );

      dafs.dafLtWeekly[year] = this.orProfileElseZoneDefValue(
        'lt',
        'weekly',
        year,
        zoneDefs,
        selectedDafProfile
      );
      dafs.dafLtMonthly[year] = this.orProfileElseZoneDefValue(
        'lt',
        'monthly',
        year,
        zoneDefs,
        selectedDafProfile
      );
    }

    return {
      dafProfileId,
      ...dafs,
      dafUnit: this.orProfileElseZoneDefUnit('adu', zoneUnits, selectedDafProfile),
      dafRedBaseUnit: this.orProfileElseZoneDefUnit('redBase', zoneUnits, selectedDafProfile),
      dafRedSafetyUnit: this.orProfileElseZoneDefUnit('redSafety', zoneUnits, selectedDafProfile),
      dafGreenUnit: this.orProfileElseZoneDefUnit('green', zoneUnits, selectedDafProfile),
      dafYellowUnit: this.orProfileElseZoneDefUnit('yellow', zoneUnits, selectedDafProfile),
      dafLtUnit: this.orProfileElseZoneDefUnit('lt', zoneUnits, selectedDafProfile)
    };
  }

  // Internal Promises callbacks
  // ~~~~

  private orProfileElseZoneDefValue(
    zone: TZone,
    frequency: 'monthly' | 'weekly',
    year: number,
    zoneDefs: IYearZoneDef,
    dp?: DafProfileModel
  ): object {
    const { id } = dp ?? {};

    const dafKey = (dafKeyFromZone(zone) + capitalize(frequency)) as keyof IDAFYearly;
    const zd = zoneDefs[year];

    const dafProfileValue = (dp?.[dafKey]?.[year] ?? {}) as object;
    let zoneDefValue = zd[zone][frequency] ?? {};
    if (frequency === 'weekly') {
      const { updatedWeeks, weekly } = zd[zone];
      const zoneDefValue: IDAFWeekly = {};
      updatedWeeks.forEach((week: keyof IDAFWeekly) => {
        zoneDefValue[`${week}`] = weekly[week];
      });
      return id ? { ...dafProfileValue } : { ...zoneDefValue };
    } else if (frequency === 'monthly') {
      const zoneDefValue = zd[zone].monthly ?? {};
      return id ? { ...dafProfileValue } : { ...zoneDefValue };
    }

    return id ? { ...dafProfileValue } : { ...zoneDefValue };
  }

  private orProfileElseZoneDefUnit(
    zone: TZone,
    zoneUnits: TZoneDefUnits,
    dp?: DafProfileModel
  ): TDAFZoneUnit {
    const { id } = dp ?? {};

    const dafKey = dafKeyFromZone(zone) + 'Unit';
    const dafProfileUnit = dp?.[dafKey as keyof DafProfileModel] as TDAFZoneUnit;
    const zoneDefUnit = zoneUnits[zone];

    return (id ? dafProfileUnit : zoneDefUnit) ?? DEFAULT_DAF_UNIT;
  }
}
